import React from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Button,
  Link,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from "@mui/material";
import {
  Download,
  Image,
  Description,
  ContactMail,
  FactCheck,
  Newspaper,
  ColorLens,
  Brush,
  Phone,
  Email
} from "@mui/icons-material";

const Press = () => {
  const theme = useTheme();

  const sections = [
    { id: "overview", title: "Overview" },
    { id: "brand-assets", title: "Brand Assets" },
    { id: "media-resources", title: "Media Resources" },
    { id: "press-releases", title: "Press Releases" },
    { id: "fact-sheet", title: "Fact Sheet" },
    { id: "contact", title: "Media Contact" }
  ];

  const brandAssets = [
    { name: "Primary Logo (PNG)", type: "PNG", size: "2.1 MB", dimensions: "2000x800" },
    { name: "Secondary Logo (PNG)", type: "PNG", size: "1.8 MB", dimensions: "500x500" },
    { name: "Icon (SVG)", type: "SVG", size: "45 KB", dimensions: "256x256" },
    { name: "Logo (White version)", type: "PNG", size: "1.2 MB", dimensions: "2000x800" },
    { name: "Brand Guidelines (PDF)", type: "PDF", size: "4.5 MB", pages: "12" }
  ];

  const screenshots = [
    { title: "Host Dashboard", description: "Overview of property management", image: "/images/press/dashboard.jpg" },
    { title: "Booking Flow", description: "Step‑by‑step booking experience", image: "/images/press/booking.jpg" },
    { title: "Cleaner App", description: "Job management and navigation", image: "/images/press/cleaner-app.jpg" },
    { title: "Messaging", description: "In‑app communication between hosts and cleaners", image: "/images/press/messaging.jpg" }
  ];

  const pressReleases = [
    { date: "March 15, 2025", title: "FreshSweeper Raises $12M Series A to Expand Nationwide", link: "#" },
    { date: "January 10, 2025", title: "FreshSweeper Launches Eco‑Friendly Cleaning Certification", link: "#" },
    { date: "October 22, 2024", title: "FreshSweeper Partners with Local Housing Authorities", link: "#" },
    { date: "August 5, 2024", title: "FreshSweeper App Named ‘Best New Utility’ by TechCrunch", link: "#" }
  ];

  const factSheet = [
    { label: "Founded", value: "2023" },
    { label: "Headquarters", value: "San Francisco, CA" },
    { label: "Number of Cleaners", value: "10,000+" },
    { label: "Number of Hosts", value: "5,000+" },
    { label: "Cities Served", value: "25+ (and growing)" },
    { label: "Average Rating", value: "4.98 / 5" },
    { label: "Jobs Completed", value: "250,000+" },
    { label: "Total Payout to Cleaners", value: "$18M+" }
  ];

  const brandColors = [
    { name: "Primary Blue", hex: "#1976D2", usage: "Buttons, links, main branding" },
    { name: "Secondary Green", hex: "#2E7D32", usage: "Success states, eco initiatives" },
    { name: "Neutral Dark", hex: "#1E1E2F", usage: "Headlines, body text" },
    { name: "Neutral Gray", hex: "#6C6C80", usage: "Secondary text, borders" },
    { name: "Background Light", hex: "#F8F9FC", usage: "Page backgrounds, cards" }
  ];

  const handleDownload = (assetName) => {
    // In a real implementation, you would trigger a file download from your server
    console.log(`Downloading ${assetName}`);
    alert(`Download started for ${assetName}. (Demo)`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Media"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Press Kit
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Everything you need to write about FreshSweeper – logos, screenshots, brand guidelines, 
          press releases, company facts, and media contact information.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* Overview */}
            <Box id="overview" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                About FreshSweeper
              </Typography>
              <Typography variant="body1" paragraph>
                FreshSweeper is a two‑sided marketplace connecting property owners (“Hosts”) with 
                vetted, professional cleaners. We make it easy to book reliable cleaning services 
                and empower independent cleaners to build their own businesses.
              </Typography>
              <Typography variant="body1">
                Founded in 2023 and headquartered in San Francisco, CA, FreshSweeper has facilitated 
                over 250,000 cleanings, paid out more than $18 million to cleaners, and maintains 
                an average rating of 4.98 stars.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Brand Assets */}
            <Box id="brand-assets" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Brand Assets
              </Typography>
              <Typography variant="body1" paragraph>
                Download official logos, icons, and brand guidelines below. Please follow our 
                <Link href="#" underline="hover" sx={{ mx: 0.5 }}>Brand Guidelines</Link>
                when using these assets.
              </Typography>
              <Grid container spacing={2}>
                {brandAssets.map((asset, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>{asset.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {asset.type} • {asset.size} • {asset.dimensions || `${asset.pages} pages`}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={() => handleDownload(asset.name)}
                        >
                          Download
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Brand Colors */}
              <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
                Brand Colors
              </Typography>
              <Grid container spacing={2}>
                {brandColors.map((color, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          backgroundColor: color.hex,
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>{color.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{color.hex}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">{color.usage}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Media Resources */}
            <Box id="media-resources" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Media Resources
              </Typography>
              <Typography variant="body1" paragraph>
                Screenshots and product images are available for download. Click any image to view 
                full size or download.
              </Typography>
              <Grid container spacing={2}>
                {screenshots.map((screenshot, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="160"
                        image={screenshot.image}
                        alt={screenshot.title}
                        sx={{ objectFit: 'cover' }}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x160?text=Placeholder'; }}
                      />
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600}>{screenshot.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{screenshot.description}</Typography>
                        <Button size="small" startIcon={<Image />} sx={{ mt: 1 }} onClick={() => handleDownload(screenshot.title)}>
                          Download
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Press Releases */}
            <Box id="press-releases" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Press Releases
              </Typography>
              <List>
                {pressReleases.map((item, idx) => (
                  <ListItem key={idx} component={Link} href={item.link} sx={{ textDecoration: 'none', color: 'inherit' }}>
                    <ListItemIcon><Newspaper color="primary" /></ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      secondary={item.date}
                    />
                    <Button size="small" variant="outlined">Read</Button>
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button variant="outlined" startIcon={<Description />} href="/press/releases/archive">
                  View All Press Releases
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Fact Sheet */}
            <Box id="fact-sheet" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Fact Sheet
              </Typography>
              <Grid container spacing={2}>
                {factSheet.map((fact, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`, py: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ width: 120 }}>{fact.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{fact.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Button
                variant="outlined"
                startIcon={<FactCheck />}
                sx={{ mt: 3 }}
                onClick={() => handleDownload("FreshSweeper_Fact_Sheet.pdf")}
              >
                Download Fact Sheet (PDF)
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Media Contact */}
            <Box id="contact" sx={{ scrollMarginTop: 80, mb: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Media Contact
              </Typography>
              <Typography variant="body1" paragraph>
                For press inquiries, interview requests, or additional information, please contact:
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 3
                }}
              >
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                  <ContactMail />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Media Relations</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Email sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    press@freshsweeper.com
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Phone sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    (555) 123-4567 ext. 101
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Email />} href="mailto:press@freshsweeper.com">
                  Send Email
                </Button>
              </Paper>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Press;